import React, { useState, useEffect } from 'react';
import { Box, Stack, CircularProgress } from '@mui/material';
import TopBar from "../../../../../../component/TopBar";
import BtnPrimary from "../../../../../../component/PrimaryButton";
import BtnSecondary from "../../../../../../component/SecondaryButton";
import FormField from "../../../../../../component/Formfield";
import { createPolicyCateogary } from "../../../../../../services/expense";
import { getPolicyCategoryById, updatePolicyCategory } from "../../../../../../services/policyCategory";
import { useNotify } from "../../../../../../component/NotificationProvider";
import { getActiveLegalUnit } from "../../../../../../services/globalData";
import { useLocation } from "react-router-dom";
import DeleteWarning from '../../../../../../component/DeleteWarning';
import ErrorMsg from '../../../../../../component/ErrorMsg';
import { currentUserDetails } from '../../../../../../utils/currentUserDetails';
import SuccessDialog from "../../../../../../component/SuccessDialog";
import { WizardCard, FormSection, FieldGrid, FieldLabel, WizardActions } from '../../../../../../component/wizard';

import { displayFont } from "../../../../../../theme";
import { tokens } from "../../../../../../theme";
const ExpenseTypeForm = () => {
  const [loading, setLoading] = useState(false);
  const [apiCall, setApiCall] = useState(false);
  const [error, setError] = useState("");
  const notify = useNotify();
  const { state } = useLocation();
  const [mode, setMode] = useState('create');
  const [typeId, setTypeId] = useState(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isSaveDialogOpen, setIsSaveDialogOpen] = useState(false);
  const [dialogMessage, setDialogMessage] = useState("");
  const user = currentUserDetails();
  const createdBy = user ? `${user.fname || ""} ${user.lname || ""}`.trim() : "—";
  const [expenseForm, setExpenseForm] = useState({ code: "", name: "", description: "" });
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    if (state && state.typeId) {
      setMode('view');
      setTypeId(state.typeId);
      fetchTypeData(state.typeId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  const fetchTypeData = async (id) => {
    setLoading(true);
    try {
      const res = await getPolicyCategoryById(id);
      const d = res.data || {};
      if (res.success) setExpenseForm((p) => ({ ...p, code: d.code, name: d.name, categoryType: d.categoryType, description: d.description }));
    } catch (error) {
      notify.error(error?.message || "Failed to load expense category");
    } finally {
      setLoading(false);
    }
  };

  const viewing = mode === 'view';
  const handleChange = (field, value) => {
    const updatedValue = field === "code" ? value?.toUpperCase() : value;
    setExpenseForm((p) => ({ ...p, [field]: updatedValue }));
    setValidationErrors((p) => ({ ...p, [field]: "" }));
  };

  const handleSaveClick = async () => {
    const errs = {};
    if (!expenseForm.code) errs.code = "Code is required";
    if (!expenseForm.name) errs.name = "Name is required";
    if (Object.keys(errs).length) { setValidationErrors(errs); return; }

    try {
      setApiCall(true);
      if (mode === 'create') {
        const legalUnit = getActiveLegalUnit();
        const payload = { ...expenseForm, type: "ANY", attributes: [], legalUnit: legalUnit?._id };
        const res = await createPolicyCateogary(payload);
        if (res.success) {
          setDialogMessage(`${expenseForm.name} category created!`);
          setExpenseForm({ code: "", name: "", description: "" });
          setIsSaveDialogOpen(true);
        } else {
          if (res.message?.includes("policy cateogary with code already exists")) setValidationErrors(prev => ({...prev, code: "An expense category with this code already exists"}));
          notify.error(res?.message || "Could not create expense category");
        }
      } else if (mode === 'edit') {
        const res = await updatePolicyCategory(typeId, expenseForm);
        if (res.success) {
          setDialogMessage(`${expenseForm.name} category updated!`);
          setExpenseForm({ code: "", name: "", description: "" });
          setMode("create");
          setIsSaveDialogOpen(true);
        } else {
          if (res.message?.includes("policy cateogary with code already exists")) setValidationErrors(prev => ({...prev, code: "An expense category with this code already exists"}));
          notify.error(res?.message || "Could not update expense category");
        }
      }
    } catch (error) {
      notify.error(error?.message || "Something went wrong saving the expense category");
    } finally {
      setApiCall(false);
    }
  };

  const title = mode === 'create' ? "Create Expense Category" : mode === 'edit' ? "Edit Expense Category" : expenseForm.name || "Expense Category";

  return (
    <Box sx={{ width: "100%" }}>
      <TopBar MainHeading={"Expense Category"} breadcrumbs={[{ label: "Expense Category", href: "/app/expense-type" }, { label: title }]} />
      <WizardCard>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: "1.5rem" }}>
          <Box sx={{ fontFamily: displayFont, fontSize: "1.25rem", fontWeight: 600 }}>{title}</Box>
          <BtnSecondary to="/app/expense-type" sx={{ borderRadius: "8px" }}>Back</BtnSecondary>
        </Stack>

        {loading ? null : (
          <FormSection title="Category details">
            <FieldGrid>
              <Box>
                <FieldLabel required>Category Code</FieldLabel>
                <FormField type="text" value={expenseForm.code} validation={validationErrors.code} disabled={viewing}
                  onChange={(e) => handleChange('code', e.target.value)} />
              </Box>
              <Box>
                <FieldLabel required>Category Name</FieldLabel>
                <FormField type="text" value={expenseForm.name} validation={validationErrors.name} disabled={viewing}
                  onChange={(e) => handleChange('name', e.target.value)} />
              </Box>
              <Box>
                <FieldLabel>Created By</FieldLabel>
                <FormField type="text" value={createdBy} disabled />
              </Box>
            </FieldGrid>
            <Box sx={{ mt: "1.25rem" }}>
              <FieldLabel>Description</FieldLabel>
              <FormField type="text" value={expenseForm.description} fullwidth row={4} disabled={viewing}
                onChange={(e) => handleChange('description', e.target.value)} />
            </Box>
          </FormSection>
        )}

        <ErrorMsg>{error}</ErrorMsg>
        <WizardActions>
          {viewing ? (
            <BtnPrimary onClick={() => setMode("edit")} sx={{ borderRadius: "8px" }}>Edit</BtnPrimary>
          ) : (
            <>
              {mode === "edit" && (
                <BtnSecondary onClick={() => setIsDeleteDialogOpen(true)} sx={{ borderRadius: "8px", color: tokens.danger, borderColor: tokens.dangerBorder, "&:hover": { borderColor: tokens.danger, background: tokens.dangerTint } }}>Delete</BtnSecondary>
              )}
              <BtnPrimary onClick={handleSaveClick} disabled={apiCall} sx={{ borderRadius: "8px" }}>
                {apiCall ? <CircularProgress size={"1.25rem"} /> : "Save Category"}
              </BtnPrimary>
            </>
          )}
        </WizardActions>

        <SuccessDialog open={isSaveDialogOpen} onClose={() => setIsSaveDialogOpen(false)}
          addNewLink={"/app/expense-type/form"} addNewText={"Create new category"}
          viewAllLink={"/app/expense-type"} viewAllText={"View all categories"} message={dialogMessage} />
        <DeleteWarning open={isDeleteDialogOpen} onClose={() => setIsDeleteDialogOpen(false)} redirectLink={"/app/expense-type"} />
      </WizardCard>
    </Box>
  );
};

export default ExpenseTypeForm;
